/**
 * Night Recap Share — shared constants, filters, and share URL helpers.
 *
 * Memories schema (verified via prod): night_date, night_name, venue_snapshots
 * [{ name, category, timeFrom }], photo_urls, created_by — no venue_name /
 * visited_at / vibe_tags columns. Venue + vibe pills map from venue_snapshots.
 */

export const NIGHT_RECAP_CARD_WIDTH = 1080;
export const NIGHT_RECAP_CARD_HEIGHT = 1920;
/** Live preview max (half of export) — filters run only at this resolution. */
export const NIGHT_RECAP_PREVIEW_MAX_WIDTH = 540;
export const NIGHT_RECAP_PREVIEW_MAX_HEIGHT = 960;

export const NIGHT_RECAP_SITE_ORIGIN = "https://pulsetonight.com";

/**
 * Landing / waitlist is `/` (no `/app` route exists).
 * Waitlist referral uses `?ref=` (verified in waitlist-signup).
 * UTM params below are feature-specific placeholders layered on that pattern.
 */
export function buildNightRecapShareUrl(options?: {
  origin?: string;
  referralCode?: string | null;
}): string {
  const origin = (options?.origin ?? NIGHT_RECAP_SITE_ORIGIN).replace(/\/$/, "");
  const url = new URL(`${origin}/`);
  url.searchParams.set("utm_source", "night_recap");
  url.searchParams.set("utm_medium", "share");
  url.searchParams.set("utm_campaign", "night_recap");
  const ref = options?.referralCode?.trim();
  if (ref) url.searchParams.set("ref", ref);
  return url.toString();
}

/**
 * Prefer `buildNightRecapShareUrl()` in captions so UTM / ref attribution is visible.
 * Bare host kept only for legacy string checks.
 */
export const NIGHT_RECAP_DISPLAY_LINK = "pulsetonight.com";

export type NightRecapVenueSnapshot = {
  name: string;
  category: string;
  timeFrom: string | null;
};

export type NightRecapMemoryInput = {
  id: string;
  nightDate: string;
  nightName: string | null;
  venueSnapshots: NightRecapVenueSnapshot[];
};

export type NightRecapCardData = {
  memoryId: string;
  /** Primary venue headline (first stop, else night name). */
  venueName: string;
  nightDate: string;
  nightName: string | null;
  /** Up to 4 category / stop labels as vibe pills. */
  vibePills: string[];
  stops: NightRecapVenueSnapshot[];
};

export function primaryVenueName(input: NightRecapMemoryInput): string {
  const first = input.venueSnapshots[0]?.name?.trim();
  if (first) return first;
  const night = input.nightName?.trim();
  if (night) return night;
  return "Pulse Night";
}

export function memoryToRecapCardData(input: NightRecapMemoryInput): NightRecapCardData {
  const pills: string[] = [];
  for (const stop of input.venueSnapshots) {
    const cat = stop.category?.trim();
    if (cat && !pills.includes(cat)) pills.push(cat);
    if (pills.length >= 4) break;
  }
  if (pills.length === 0) {
    for (const stop of input.venueSnapshots.slice(0, 3)) {
      if (stop.name?.trim()) pills.push(stop.name.trim());
    }
  }
  return {
    memoryId: input.id,
    venueName: primaryVenueName(input),
    nightDate: input.nightDate,
    nightName: input.nightName,
    vibePills: pills.slice(0, 4),
    stops: input.venueSnapshots,
  };
}

/** Slider range −50..+50, default 0. */
export type NightRecapFilterAdjustments = {
  brightness: number;
  contrast: number;
  saturation: number;
  /** Degrees — used by Warm / Cool presets. */
  hueRotate: number;
};

export const NIGHT_RECAP_FILTER_DEFAULT: NightRecapFilterAdjustments = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  hueRotate: 0,
};

export type NightRecapFilterPresetId = "original" | "warm" | "cool" | "highContrast";

export const NIGHT_RECAP_FILTER_PRESETS: Record<
  NightRecapFilterPresetId,
  NightRecapFilterAdjustments
> = {
  original: { ...NIGHT_RECAP_FILTER_DEFAULT },
  warm: { brightness: 6, contrast: 8, saturation: 18, hueRotate: -18 },
  cool: { brightness: 0, contrast: 6, saturation: 8, hueRotate: 22 },
  highContrast: { brightness: -4, contrast: 38, saturation: 12, hueRotate: 0 },
};

function clampSlider(n: number): number {
  return Math.max(-50, Math.min(50, Math.round(n)));
}

export function normalizeFilterAdjustments(
  partial: Partial<NightRecapFilterAdjustments>,
): NightRecapFilterAdjustments {
  return {
    brightness: clampSlider(partial.brightness ?? 0),
    contrast: clampSlider(partial.contrast ?? 0),
    saturation: clampSlider(partial.saturation ?? 0),
    hueRotate: Math.max(-180, Math.min(180, Math.round(partial.hueRotate ?? 0))),
  };
}

/** Map −50..+50 → multiplier factor around 1 (e.g. +50 → 1.5). */
export function sliderToFactor(slider: number): number {
  return 1 + clampSlider(slider) / 100;
}

/** CSS `filter` string for live preview + canvas bake. */
export function nightRecapCssFilter(adj: NightRecapFilterAdjustments): string {
  const b = sliderToFactor(adj.brightness);
  const c = sliderToFactor(adj.contrast);
  const s = sliderToFactor(adj.saturation);
  const parts = [`brightness(${b})`, `contrast(${c})`, `saturate(${s})`];
  if (adj.hueRotate !== 0) parts.push(`hue-rotate(${adj.hueRotate}deg)`);
  return parts.join(" ");
}

/**
 * Skia / RN ColorMatrix (row-major 4×5 = 20 floats).
 * Combines brightness, contrast, saturation; hue-rotate approximated via
 * warm/cool presets using a light channel bias when |hue| is large.
 */
export function nightRecapColorMatrix(adj: NightRecapFilterAdjustments): number[] {
  const b = adj.brightness / 100;
  const contrast = sliderToFactor(adj.contrast);
  const sat = sliderToFactor(adj.saturation);

  // Saturation matrix (luma weights)
  const lr = 0.2126;
  const lg = 0.7152;
  const lb = 0.0722;
  const sr = (1 - sat) * lr;
  const sg = (1 - sat) * lg;
  const sb = (1 - sat) * lb;

  let m = [
    sr + sat,
    sg,
    sb,
    0,
    0,
    sr,
    sg + sat,
    sb,
    0,
    0,
    sr,
    sg,
    sb + sat,
    0,
    0,
    0,
    0,
    0,
    1,
    0,
  ];

  // Contrast around mid-gray
  const t = (1 - contrast) / 2;
  m = multiplyColorMatrices(
    [contrast, 0, 0, 0, t, 0, contrast, 0, 0, t, 0, 0, contrast, 0, t, 0, 0, 0, 1, 0],
    m,
  );

  // Brightness as additive
  m = multiplyColorMatrices(
    [1, 0, 0, 0, b, 0, 1, 0, 0, b, 0, 0, 1, 0, b, 0, 0, 0, 1, 0],
    m,
  );

  // Warm / cool tint from hueRotate magnitude
  if (adj.hueRotate !== 0) {
    const warm = adj.hueRotate < 0 ? Math.min(1, Math.abs(adj.hueRotate) / 40) : 0;
    const cool = adj.hueRotate > 0 ? Math.min(1, adj.hueRotate / 40) : 0;
    m = multiplyColorMatrices(
      [
        1 + 0.08 * warm,
        0,
        0,
        0,
        0.04 * warm,
        0,
        1 + 0.02 * warm - 0.02 * cool,
        0,
        0,
        0,
        0,
        0,
        1 + 0.1 * cool,
        0,
        0.03 * cool,
        0,
        0,
        0,
        1,
        0,
      ],
      m,
    );
  }

  return m;
}

function multiplyColorMatrices(a: number[], b: number[]): number[] {
  const out = new Array<number>(20).fill(0);
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 5; col++) {
      out[row * 5 + col] =
        a[row * 5]! * b[col]! +
        a[row * 5 + 1]! * b[5 + col]! +
        a[row * 5 + 2]! * b[10 + col]! +
        a[row * 5 + 3]! * b[15 + col]! +
        (col === 4 ? a[row * 5 + 4]! : 0);
    }
  }
  return out;
}

export function formatNightRecapDate(
  nightDate: string,
  locale: string,
): string {
  const d = new Date(`${nightDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return nightDate;
  return d.toLocaleDateString(locale, {
    weekday: "short",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
